import _ from 'lodash'
import moment from 'moment'
import React from 'react'

import { DISPLAY_SHORT_DOT_DELAMINATE_DATE_FORMAT } from 'constants/date'
import { BasicViewProps, Option } from 'constants/models'
import { MF_SIGNATURE_TYPES } from 'services/medicationForms/constants'
import { REVIEW_MEDICATION_STATUSES } from 'services/reviews/constants'
import { ReviewHistoryData } from 'services/reviews/models'

import {
  Avatar,
  DetailsBreakdown,
  EmptyState,
  Form,
  InfiniteDropdowns,
  InfiniteScroll,
  Page,
  Spinner,
  Toolbar,
  Typography,
} from 'components'
import { SignaturePreview } from 'module/Safeguarding/components'

import i18n from 'translations'

import {
  StyledContent,
  StyledField,
  StyledHeader,
  StyledItem,
  StyledRightColumn,
  StyledSignatureValueContainer,
  StyledValue,
} from './HistoryStyle'

interface HistoryViewProps extends BasicViewProps {
  isFetching?: boolean
  onPageChange: (page: number) => void
  onStaffChange: (selectedItem: Option) => void
  page?: number
  pageCount?: number
  reviewHistoryData?: ReviewHistoryData[]
  staff?: Option
}

const HistoryView: React.FC<HistoryViewProps> = ({
  isFetching,
  onPageChange,
  onStaffChange,
  page,
  pageCount,
  reviewHistoryData,
  staff,
}) => {
  const renderBreakdownItem = ({ label, padding = '0 0 15px 0', value }) => (
    <DetailsBreakdown.Item
      label={label}
      margin="0"
      padding={padding}
      proportion="300px auto"
      value={value || '-'}
      withoutBorder
    />
  )

  const renderSignatureDetails = (signatures) => {
    const parent = _.find(signatures, { type: MF_SIGNATURE_TYPES.PARENT })
    const staffMember = _.find(signatures, { type: MF_SIGNATURE_TYPES.STAFF })

    return (
      <React.Fragment>
        <Typography fontSize={20} margin="15px 0" bold>
          {i18n.t('module:Safeguarding:MedicationForm:Preview:Headers:Signature:title')}
        </Typography>
        {staffMember && (
          <React.Fragment>
            {renderBreakdownItem({
              label: i18n.t('module:Safeguarding:MedicationForm:Preview:Headers:Signature:Staff:administratedBy'),
              padding: '0',
              value: (
                <StyledSignatureValueContainer>
                  <div>
                    <Avatar
                      avatarSize="small"
                      initials={staffMember.owner.name.split(' ')}
                      src={staffMember.owner.photo}
                      title={staffMember.owner.name}
                    />
                  </div>
                  <SignaturePreview signedAt={staffMember.signedAt} url={staffMember.signature.url} />
                </StyledSignatureValueContainer>
              ),
            })}
          </React.Fragment>
        )}
        {parent && (
          <React.Fragment>
            {renderBreakdownItem({
              label: i18n.t('module:Safeguarding:MedicationForm:Preview:Headers:Signature:Parent:parent'),
              padding: '0',
              value: (
                <StyledSignatureValueContainer>
                  <div>
                    <Avatar
                      avatarSize="small"
                      initials={[parent.carer.firstName, parent.carer.surname]}
                      src={parent.carer.photo}
                      title={`${parent.carer.firstName} ${parent.carer.surname}`}
                    />
                  </div>
                  <SignaturePreview signedAt={parent.signedAt} url={parent.signature.url} />
                </StyledSignatureValueContainer>
              ),
            })}
          </React.Fragment>
        )}
      </React.Fragment>
    )
  }

  const renderDateHistory = (title: string, oldValue: string, newValue: string) => (
    <StyledField>
      <Typography fontSize={14} bold>
        {title}
      </Typography>
      <StyledRightColumn>
        <React.Fragment>
          <StyledValue $old>
            {oldValue}
          </StyledValue>
          <StyledValue>
            {newValue}
          </StyledValue>
        </React.Fragment>
      </StyledRightColumn>
    </StyledField>
  )

  const renderItem = (item: ReviewHistoryData, index: number) => {
    const {
      author,
      createdAt,
      endMedicationDate,
      medicationReturnedDate,
      nextReviewDate,
      notes,
      signatures,
      type,
    } = item

    const isMedicationStillNeeded = REVIEW_MEDICATION_STATUSES.MEDICATION_IS_STILL_NEEDED === type

    return (
      <StyledItem key={index}>
        <StyledHeader>
          <Typography bold>
            {i18n.t('module:Safeguarding:MedicationForm:Preview:ReviewForm:title')}
            {' - '}
            {isMedicationStillNeeded
              ? i18n.t('services:Review:ReviewMedication:medicationStillNeeded')
              : i18n.t('services:Review:ReviewMedication:endMedication')}
          </Typography>
          <Typography fontSize={14} bold>
            {author?.name}
            &nbsp;&nbsp;&nbsp;&nbsp;
            {moment(createdAt).format('DD MMMM YYYY')}
          </Typography>
        </StyledHeader>
        <StyledContent>
          {isMedicationStillNeeded
            ? (
              renderDateHistory(
                i18n.t('module:Safeguarding:MedicationForm:Preview:ReviewForm:NextScheduledReviewDate:label'),
                moment(nextReviewDate.oldValue).format(DISPLAY_SHORT_DOT_DELAMINATE_DATE_FORMAT),
                moment(nextReviewDate.newValue).format(DISPLAY_SHORT_DOT_DELAMINATE_DATE_FORMAT),
              )
            ) : (
              <React.Fragment>
                {renderDateHistory(
                  i18n.t('module:Safeguarding:MedicationForm:Preview:ReviewForm:MedicationFormEndDate:label'),
                  moment(endMedicationDate.oldValue).format(DISPLAY_SHORT_DOT_DELAMINATE_DATE_FORMAT),
                  moment(endMedicationDate.newValue).format(DISPLAY_SHORT_DOT_DELAMINATE_DATE_FORMAT),
                )}
                {renderDateHistory(
                  i18n.t('module:Safeguarding:MedicationForm:Preview:ReviewForm:DateMedicationReturned:label'),
                  moment(medicationReturnedDate.oldValue).format(DISPLAY_SHORT_DOT_DELAMINATE_DATE_FORMAT),
                  moment(medicationReturnedDate.newValue).format(DISPLAY_SHORT_DOT_DELAMINATE_DATE_FORMAT),
                )}
              </React.Fragment>
            )}
          <StyledField>
            <Typography fontSize={14} bold>
              {i18n.t('module:Safeguarding:MedicationForm:Preview:ReviewForm:ReviewNotes:label')}
            </Typography>
            <StyledRightColumn>
              <Typography>{notes}</Typography>
            </StyledRightColumn>
          </StyledField>
          {!isMedicationStillNeeded && renderSignatureDetails(signatures)}
        </StyledContent>
      </StyledItem>
    )
  }

  const renderContent = () => {
    if (isFetching && 1 === page) {
      return (
        <Spinner />
      )
    }

    if (!reviewHistoryData?.length) {
      return (
        <EmptyState
          icon="medication-form-history"
          text1={i18n.t('module:Safeguarding:MedicationForm:Preview:History:emptyStateText')}
        />
      )
    }

    return (
      <InfiniteScroll
        dataLength={reviewHistoryData?.length || 0}
        hasMore={page < pageCount}
        isFetching={isFetching}
        next={() => onPageChange((+page) + 1)}
      >
        {_.map(reviewHistoryData, renderItem)}
      </InfiniteScroll>
    )
  }

  return (
    <Page.Section title={i18n.t('module:Safeguarding:MedicationForm:Preview:History:title')}>
      <Toolbar>
        <Toolbar.Group>
          <Toolbar.Item>
            <Form.Row label={i18n.t('module:Safeguarding:MedicationForm:Preview:History:updatedMadeBy')} verticalLabel>
              <InfiniteDropdowns.Memberships
                placeholder={i18n.t('global:All')}
                value={staff}
                onChange={onStaffChange}
              />
            </Form.Row>
          </Toolbar.Item>
        </Toolbar.Group>
      </Toolbar>
      {renderContent()}
    </Page.Section>
  )
}

export default HistoryView
